import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ThumbsDown, ThumbsUp, X, Loader2 } from 'lucide-react';
import { postPointFeedback } from '../../api/beachvision';
import toast from 'react-hot-toast';

const REASON_OPTIONS = [
  { code: 'wrong_winner', label: 'Wrong winner' },
  { code: 'bad_touch_log', label: 'Play-by-play' },
  { code: 'bad_summary', label: 'Summary' },
  { code: 'bad_takeaways', label: 'Takeaways' },
  { code: 'roster_mismatch', label: 'Players' },
  { code: 'inconclusive_footage', label: 'Footage unclear' },
  { code: 'other', label: 'Other' },
];

const POLL_MS = 5000;

function isRevalidationRisk(status) {
  return status === 'warn' || status === 'failed';
}

/**
 * @param {object} props
 * @param {object} props.point
 * @param {number} props.analysisId
 * @param {() => Promise<void>} [props.onRefresh]
 */
export function PointValidationFeedback({ point, analysisId, onRefresh }) {
  const rev = Number(point?.analysis_revision ?? 1) || 1;
  const status = point?.validation_status ?? 'pending';
  const reanalysisBusy = Boolean(point?.reanalysis_in_progress);
  const scoring = point?.processing_status !== 'complete';
  const actionsDisabled = scoring || reanalysisBusy;

  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState(() => new Set());
  const [comment, setComment] = useState('');
  const [requestReanalysis, setRequestReanalysis] = useState(true);

  const uf = point?.user_feedback;

  useEffect(() => {
    if (modalOpen) {
      const pre = (point?.suggested_feedback_text || '').trim();
      setComment(pre);
      setRequestReanalysis(true);
      setSelectedReasons(new Set());
    }
  }, [modalOpen, point?.suggested_feedback_text, point?.id]);

  useEffect(() => {
    if (!reanalysisBusy || !onRefresh) return undefined;
    onRefresh();
    const id = setInterval(() => onRefresh(), POLL_MS);
    return () => clearInterval(id);
  }, [reanalysisBusy, onRefresh, point?.id]);

  const suggestedDefault = (point?.suggested_feedback_text || '').trim();
  const serverOk = status === 'pass';
  const risky = isRevalidationRisk(status);
  const thumbsUpForRev =
    uf?.rating === 'thumbs_up' && Number(uf?.analysis_revision) === rev;

  const showMisscoreBanner = useMemo(() => {
    if (reanalysisBusy) return false;
    if (serverOk) return false;
    if (thumbsUpForRev) return false;
    if (risky) return true;
    return false;
  }, [reanalysisBusy, serverOk, thumbsUpForRev, risky]);

  const message =
    (point?.validation_warning_message && String(point.validation_warning_message).trim()) ||
    'We are less confident this point is scored right. If it actually looks right, use the thumb up, or let us know what to fix.';

  const doSubmit = useCallback(
    async (rating, extra) => {
      if (!analysisId || !point?.id) return;
      setSubmitting(true);
      try {
        await postPointFeedback(analysisId, point.id, { rating, ...extra });
        if (onRefresh) await onRefresh();
        if (rating === 'thumbs_up') {
          toast.success('Thanks for the signal.');
        } else {
          toast.success('Feedback received.');
        }
      } catch (e) {
        const d = e?.response?.data;
        const msg =
          (typeof d?.detail === 'string' && d.detail) ||
          d?.message ||
          e?.message ||
          'Could not send feedback';
        toast.error(String(msg).slice(0, 200));
      } finally {
        setSubmitting(false);
      }
    },
    [analysisId, onRefresh, point?.id]
  );

  const onThumbsUp = () => {
    if (submitting || actionsDisabled) return;
    setModalOpen(false);
    void doSubmit('thumbs_up', {
      reason_codes: [],
      comment_text: '',
      request_reanalysis: false,
    });
  };

  const onModalSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    setModalOpen(false);
    const codes = [...selectedReasons];
    void doSubmit('thumbs_down', {
      reason_codes: codes,
      comment_text: comment || '',
      request_reanalysis: requestReanalysis,
    });
  };

  const onThumbsDownClick = () => {
    if (submitting || actionsDisabled) return;
    setModalOpen(true);
  };

  if (!point || !analysisId) return null;
  if (point.processing_status === 'failed' && !point?.point_summary) {
    return null;
  }

  const upSelected = uf?.rating === 'thumbs_up' && Number(uf?.analysis_revision) === rev;
  const downSelected = uf?.rating === 'thumbs_down' && Number(uf?.analysis_revision) === rev;

  return (
    <div className="mt-4">
      {reanalysisBusy && (
        <p
          className="text-xs text-beachvision-teal/90 font-display font-semibold mb-2 flex items-center gap-2"
          role="status"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Re-scoring this point&hellip; this can take a minute.
        </p>
      )}

      {showMisscoreBanner && (
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 text-amber-200/90 text-sm px-3 py-2.5 mb-3 leading-relaxed"
          role="status"
        >
          {message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onThumbsUp}
          disabled={submitting || actionsDisabled}
          className={`flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 font-display font-bold text-sm transition-colors
            ${
              upSelected
                ? 'bg-beachvision-teal/10 border-beachvision-teal text-beachvision-teal shadow-[0_0_20px_rgba(78,205,196,0.25)]'
                : 'bg-transparent border-beachvision-teal/50 text-beachvision-teal/80 hover:border-beachvision-teal/80'
            }`}
        >
          <ThumbsUp
            className="h-6 w-6"
            strokeWidth={upSelected ? 2.5 : 2}
            fill={upSelected ? 'currentColor' : 'none'}
            aria-hidden
          />
          Looks right
        </button>
        <button
          type="button"
          onClick={onThumbsDownClick}
          disabled={submitting || actionsDisabled}
          className={`flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 font-display font-bold text-sm transition-colors
            ${
              downSelected
                ? 'bg-beachvision-coral/10 border-beachvision-coral text-beachvision-coral shadow-[0_0_20px_rgba(244,132,95,0.2)]'
                : 'bg-transparent border-beachvision-coral/50 text-beachvision-coral/85 hover:border-beachvision-coral/80'
            }`}
        >
          <ThumbsDown
            className="h-6 w-6"
            strokeWidth={downSelected ? 2.5 : 2}
            fill={downSelected ? 'currentColor' : 'none'}
            aria-hidden
          />
          Issue
        </button>
      </div>
      {scoring && !reanalysisBusy && (
        <p className="text-[11px] text-beachvision-muted mt-2">Scoring&hellip; feedback is available when this point is ready.</p>
      )}

      {typeof document !== 'undefined' && modalOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 bg-black/60 backdrop-blur-sm"
              role="presentation"
              onKeyDown={(ev) => {
                if (ev.key === 'Escape') setModalOpen(false);
              }}
              onClick={() => setModalOpen(false)}
            >
              <div
                className="w-full max-w-md rounded-2xl border border-white/10 bg-beachvision-surface-low shadow-2xl p-4"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="point-feedback-title"
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h3 id="point-feedback-title" className="font-display font-bold text-white text-base">
                    What was off?
                  </h3>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="p-2 rounded-lg text-beachvision-muted hover:text-white"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                {suggestedDefault && (
                  <p className="text-xs text-beachvision-muted mb-3 leading-relaxed">{suggestedDefault}</p>
                )}
                <form onSubmit={onModalSubmit} className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {REASON_OPTIONS.map((o) => {
                      const on = selectedReasons.has(o.code);
                      return (
                        <label
                          key={o.code}
                          className={`text-[11px] px-2.5 py-1.5 rounded-full font-display font-semibold cursor-pointer border
                            ${
                              on
                                ? 'border-beachvision-coral text-beachvision-coral bg-beachvision-coral/10'
                                : 'border-white/10 text-beachvision-muted'
                            }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={on}
                            onChange={() => {
                              setSelectedReasons((prev) => {
                                const n = new Set(prev);
                                if (n.has(o.code)) n.delete(o.code);
                                else n.add(o.code);
                                return n;
                              });
                            }}
                          />
                          {o.label}
                        </label>
                      );
                    })}
                  </div>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Optional details (we read these)"
                    rows={3}
                    className="w-full rounded-xl bg-beachvision-bg/80 border border-white/10 text-sm text-beachvision-muted placeholder:text-beachvision-muted/50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-beachvision-coral/30"
                  />
                  <label className="flex items-center gap-2 text-sm text-beachvision-muted cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-white/20 text-beachvision-coral"
                      checked={requestReanalysis}
                      onChange={(e) => setRequestReanalysis(e.target.checked)}
                    />
                    Re-run this point&rsquo;s AI scoring
                  </label>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setModalOpen(false)}
                      className="flex-1 py-2.5 rounded-xl border border-white/10 text-beachvision-muted text-sm font-display font-bold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 py-2.5 rounded-xl bg-beachvision-coral/20 border border-beachvision-coral text-beachvision-coral text-sm font-display font-bold"
                    >
                      {submitting ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
